import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  MATCOLS,
  NEWD,
  REHAB,
  UNITS,
  componentList,
  dShort,
  fmt,
  pct1,
  statusColors,
  type MaterialRow,
  type Week,
} from '@piaip/shared';
import { sx, sxc } from '../sx';
import { useApp } from '../state';
import { api } from '../api';
import {
  BTN_DANGER,
  BTN_PRIMARY,
  BTN_SOFT,
  BTN_X,
  CARD,
  Field,
  HINT,
  INPUT,
  INPUT_CELL,
  INPUT_NUM,
  SERIF_H,
  TD_CELL,
  TH,
  TH_S,
} from '../components/ui';

const STATUS_WORD: Record<string, string> = {
  draft: 'Draft — not yet submitted',
  submitted: 'Submitted — awaiting PMU approval',
  approved: 'Approved by PMU',
  returned: 'Returned for correction',
};

function trailOf(w: Week): string {
  const bits: string[] = [];
  if (w.submittedAt) bits.push(`submitted by ${w.submittedBy || '—'} on ${dShort(w.submittedAt)}`);
  if (w.approvedAt && w.status === 'approved')
    bits.push(`approved by ${w.approvedBy || '—'} on ${dShort(w.approvedAt)}`);
  if (w.returnedAt && w.status === 'returned')
    bits.push(`returned by ${w.returnedBy || '—'} on ${dShort(w.returnedAt)}`);
  return bits.length ? bits.join(' · ') : 'No approval action yet';
}

const SAVE_DELAY_MS = 700;

export function Entry({
  focusWeek,
  onFocusHandled,
}: {
  focusWeek: string | null;
  onFocusHandled: () => void;
}) {
  const { engine, can, mutate, notify, refresh } = useApp();
  const [selected, setSelected] = useState<string>('');

  const sorted = useMemo(() => engine.sortedWeeks(), [engine]);

  // Fall back to the newest week the account can still work on.
  useEffect(() => {
    if (focusWeek) {
      setSelected(focusWeek);
      onFocusHandled();
      return;
    }
    if (!sorted.length) {
      setSelected('');
      return;
    }
    if (!sorted.some((w) => w.id === selected)) {
      const editable = sorted.filter((w) => w.status === 'draft' || w.status === 'returned');
      setSelected((editable[editable.length - 1] ?? sorted[sorted.length - 1]).id);
    }
  }, [focusWeek, sorted, selected, onFocusHandled]);

  const week = sorted.find((w) => w.id === selected) ?? null;
  const canEntry = can('entry');
  const editable = !!week && canEntry && (week.status === 'draft' || week.status === 'returned');

  // ---------------------------------------------------------- local draft
  const [draft, setDraft] = useState<Week | null>(week);
  const timer = useRef<number | undefined>(undefined);
  const draftRef = useRef<Week | null>(draft);
  draftRef.current = draft;

  // Every keystroke bumps the sequence; a save only marks the draft clean if
  // nothing else was typed while the request was in flight.
  const editSeq = useRef(0);
  const savedSeq = useRef(0);
  const dirty = useRef(false);
  dirty.current = editSeq.current !== savedSeq.current;

  useEffect(() => {
    // A periodic refresh must never overwrite what is being typed.
    if (dirty.current && draftRef.current?.id === week?.id) return;
    setDraft(week ? structuredClone(week) : null);
  }, [week]);

  const flush = useCallback(async () => {
    const d = draftRef.current;
    if (!d) return;
    window.clearTimeout(timer.current);
    const seq = editSeq.current;
    await mutate(
      () =>
        api.patchWeek(d.id, {
          no: d.no,
          start: d.start,
          end: d.end,
          doc: d.doc,
          narrative: d.narrative,
          drainsNew: d.drainsNew,
          drainsRehab: d.drainsRehab,
          remarksNew: d.remarksNew,
          remarksRehab: d.remarksRehab,
          units: d.units,
          unitRemarks: d.unitRemarks,
          material: d.material,
          expenses: d.expenses,
        }),
      { refresh: false },
    );
    if (editSeq.current === seq) savedSeq.current = seq;
  }, [mutate]);

  const edit = useCallback(
    (fn: (d: Week) => void) => {
      setDraft((prev) => {
        if (!prev) return prev;
        const next = structuredClone(prev);
        fn(next);
        return next;
      });
      editSeq.current += 1;
      window.clearTimeout(timer.current);
      timer.current = window.setTimeout(() => void flush(), SAVE_DELAY_MS);
    },
    [flush],
  );

  // Never leave an edit unsaved when the window goes away.
  useEffect(() => {
    const onHide = () => {
      if (dirty.current) void flush();
    };
    window.addEventListener('pagehide', onHide);
    document.addEventListener('visibilitychange', onHide);
    return () => {
      window.removeEventListener('pagehide', onHide);
      document.removeEventListener('visibilitychange', onHide);
      if (dirty.current) void flush();
    };
  }, [flush]);

  const workflow = async (fn: () => Promise<unknown>) => {
    if (dirty.current) await flush();
    await mutate(fn);
  };

  const addWeek = async () => {
    const created = await mutate(() => api.createWeek());
    if (created) {
      setSelected(created.id);
      notify(`Week ${created.no} created.`);
    }
  };

  const wsc = statusColors(week?.status);
  const dis = !editable;

  return (
    <div
      style={sx(
        'max-width:1180px;margin:0 auto;padding:20px 16px 60px;display:flex;flex-direction:column;gap:18px',
      )}
    >
      <div
        style={sx(
          'background:#fff;border:1px solid #dbe3e0;border-radius:8px;padding:16px;display:flex;gap:14px;align-items:flex-end;flex-wrap:wrap',
        )}
      >
        <Field
          label="Reporting week"
          htmlFor="pms-week"
          style="display:flex;flex-direction:column;gap:5px;flex:1;min-width:220px"
        >
          <select
            id="pms-week"
            value={selected}
            onChange={(e) => setSelected(e.target.value)}
            style={sx(
              'padding:10px;border:1px solid #d3dcda;border-radius:4px;font-size:14px;width:100%;box-sizing:border-box;background:#fff',
            )}
          >
            {sorted.map((w) => (
              <option key={w.id} value={w.id}>
                Week {w.no} — {dShort(w.start)} to {dShort(w.end)} ({w.status || 'draft'})
              </option>
            ))}
          </select>
        </Field>
        {canEntry ? (
          <button type="button" onClick={() => void addWeek()} style={sx(BTN_PRIMARY)}>
            + New week
          </button>
        ) : null}
        <div style={sx('font-size:12px;color:#5a6b68;max-width:340px;line-height:1.5;flex:1;min-width:200px')}>
          Enter only this week&rsquo;s increment, exactly as on the field proforma. Cumulative totals
          are worked out automatically.
        </div>
      </div>

      {!week || !draft ? (
        <div
          style={sx(
            'background:#fff;border:1px dashed #c3d0cd;border-radius:8px;padding:44px 20px;text-align:center',
          )}
        >
          <div
            style={sx(
              "font-family:'IBM Plex Serif',serif;font-size:18px;font-weight:600;margin-bottom:8px",
            )}
          >
            No reporting weeks yet
          </div>
          <div style={sx('font-size:13px;color:#5a6b68;margin-bottom:18px')}>
            {canEntry
              ? 'Add Week 1 and work forward — the system builds the cumulative picture as you go.'
              : 'No weekly return has been recorded yet.'}
          </div>
          {canEntry ? (
            <button
              type="button"
              onClick={() => void addWeek()}
              style={sx(
                'background:#0f5c52;color:#fff;border:0;padding:12px 20px;border-radius:4px;font-size:14px;font-weight:600;cursor:pointer',
              )}
            >
              Add first week
            </button>
          ) : null}
        </div>
      ) : (
        <div style={sx('display:flex;flex-direction:column;gap:18px')}>
          <div
            style={sxc(
              'border-radius:8px;padding:14px 16px;display:flex;gap:14px;align-items:center;flex-wrap:wrap',
              `background:${wsc.bg}`,
              `border:1px solid ${wsc.fg}`,
            )}
          >
            <div style={sx('display:flex;flex-direction:column;gap:3px;flex:1;min-width:220px')}>
              <div style={sxc('font-size:14px;font-weight:700', `color:${wsc.fg}`)}>
                {STATUS_WORD[week.status ?? 'draft']}
              </div>
              <div style={sx('font-size:12.5px;color:#33403d;line-height:1.5')}>
                {trailOf(week)}
                {week.reviewNote ? ` · Returned with the remark: ${week.reviewNote}` : ''}
                {!canEntry ? ' · Your account has read-only access to weekly entry.' : ''}
              </div>
            </div>
            <div style={sx('display:flex;gap:8px;flex-wrap:wrap')}>
              {canEntry && (week.status === 'draft' || week.status === 'returned') ? (
                <button
                  type="button"
                  onClick={() => void workflow(() => api.submitWeek(week.id))}
                  style={sx(
                    'background:#0f5c52;color:#fff;border:0;padding:10px 16px;border-radius:4px;font-size:13.5px;font-weight:600;cursor:pointer',
                  )}
                >
                  Submit for approval
                </button>
              ) : null}
              {can('approve') && week.status === 'submitted' ? (
                <button
                  type="button"
                  onClick={() => void workflow(() => api.approveWeek(week.id))}
                  style={sx(
                    'background:#2f7d51;color:#fff;border:0;padding:10px 16px;border-radius:4px;font-size:13.5px;font-weight:600;cursor:pointer',
                  )}
                >
                  Approve
                </button>
              ) : null}
              {can('approve') && (week.status === 'submitted' || week.status === 'approved') ? (
                <button
                  type="button"
                  onClick={() => {
                    const note = window.prompt('What must the Field Officer correct?');
                    if (note !== null) void workflow(() => api.returnWeek(week.id, note));
                  }}
                  style={sx(
                    'background:#fff;color:#a3372c;border:1px solid #e3c9c5;padding:10px 16px;border-radius:4px;font-size:13.5px;font-weight:600;cursor:pointer',
                  )}
                >
                  Return for correction
                </button>
              ) : null}
            </div>
          </div>

          <div
            style={sx(
              'background:#fff;border:1px solid #dbe3e0;border-radius:8px;padding:18px;display:grid;grid-template-columns:repeat(auto-fit,minmax(150px,1fr));gap:14px',
            )}
          >
            <Field label="Week no." htmlFor="w-no">
              <input
                id="w-no"
                type="number"
                value={String(draft.no ?? '')}
                disabled={dis}
                onChange={(e) => edit((d) => void (d.no = e.target.value))}
                style={sx(INPUT)}
              />
            </Field>
            <Field label="Week start" htmlFor="w-start">
              <input
                id="w-start"
                type="date"
                value={draft.start ?? ''}
                disabled={dis}
                onChange={(e) => edit((d) => void (d.start = e.target.value))}
                style={sx(INPUT)}
              />
            </Field>
            <Field label="Week end" htmlFor="w-end">
              <input
                id="w-end"
                type="date"
                value={draft.end ?? ''}
                disabled={dis}
                onChange={(e) => edit((d) => void (d.end = e.target.value))}
                style={sx(INPUT)}
              />
            </Field>
            <Field label="Document ID" htmlFor="w-doc">
              <input
                id="w-doc"
                type="text"
                value={draft.doc ?? ''}
                disabled={dis}
                onChange={(e) => edit((d) => void (d.doc = e.target.value))}
                style={sx(
                  "padding:9px 10px;border:1px solid #d3dcda;border-radius:4px;font-size:13px;width:100%;box-sizing:border-box;font-family:'IBM Plex Mono',monospace",
                )}
              />
            </Field>
          </div>

          {(
            [
              {
                title: 'Civil work — construction of new drains',
                note: 'Length excavated during this week only.',
                kind: 'drainsNew' as const,
                rk: 'remarksNew' as const,
                list: NEWD,
              },
              {
                title: 'Civil work — rehabilitation of existing drains',
                note: 'Length rehabilitated during this week only.',
                kind: 'drainsRehab' as const,
                rk: 'remarksRehab' as const,
                list: REHAB,
              },
            ] as const
          ).map((sec) => (
            <div key={sec.kind} style={sx(CARD)}>
              <div style={sxc(SERIF_H, 'margin-bottom:2px')}>{sec.title}</div>
              <div style={sxc(HINT, 'margin-bottom:14px')}>{sec.note}</div>
              <div style={sx('overflow-x:auto')}>
                <table style={sx('width:100%;border-collapse:collapse;min-width:700px')}>
                  <thead>
                    <tr>
                      <th style={sx(TH)}>Drain</th>
                      <th style={sxc(TH, 'text-align:right')}>Total (ft)</th>
                      <th style={sxc(TH, 'text-align:right;width:130px')}>This week (ft)</th>
                      <th style={sxc(TH, 'text-align:right')}>Approved to date</th>
                      <th style={sxc(TH, 'text-align:right')}>%</th>
                      <th style={sx(TH)}>Remarks</th>
                    </tr>
                  </thead>
                  <tbody>
                    {sec.list.map((d) => {
                      const cum = Math.min(d.ft, engine.cumDrain(sec.kind, d.k));
                      const p = (cum / d.ft) * 100;
                      return (
                        <tr key={d.k}>
                          <td style={sx('padding:6px 10px;border-bottom:1px solid #eef2f1;font-size:13px')}>
                            {d.n}
                          </td>
                          <td
                            style={sx(
                              "padding:6px 10px;border-bottom:1px solid #eef2f1;font-size:13px;text-align:right;font-family:'IBM Plex Mono',monospace;color:#5a6b68",
                            )}
                          >
                            {fmt(d.ft)}
                          </td>
                          <td style={sx('padding:6px 10px;border-bottom:1px solid #eef2f1')}>
                            <input
                              type="number"
                              aria-label={`${d.n} — length this week, feet`}
                              value={String(draft[sec.kind][d.k] ?? '')}
                              disabled={dis}
                              onChange={(e) =>
                                edit((x) => {
                                  if (e.target.value === '') delete x[sec.kind][d.k];
                                  else x[sec.kind][d.k] = e.target.value;
                                })
                              }
                              style={sx(INPUT_NUM)}
                            />
                          </td>
                          <td
                            style={sx(
                              "padding:6px 10px;border-bottom:1px solid #eef2f1;font-size:13px;text-align:right;font-family:'IBM Plex Mono',monospace",
                            )}
                          >
                            {fmt(cum)}
                          </td>
                          <td
                            style={sxc(
                              "padding:6px 10px;border-bottom:1px solid #eef2f1;font-size:13px;text-align:right;font-family:'IBM Plex Mono',monospace",
                              `color:${p >= 100 ? '#2f7d51' : '#12211f'}`,
                            )}
                          >
                            {pct1(p)}%
                          </td>
                          <td style={sx('padding:6px 10px;border-bottom:1px solid #eef2f1')}>
                            <input
                              type="text"
                              aria-label={`${d.n} — remarks`}
                              value={draft[sec.rk][d.k] ?? ''}
                              disabled={dis}
                              onChange={(e) => edit((x) => void (x[sec.rk][d.k] = e.target.value))}
                              style={sxc(INPUT_CELL, 'min-width:120px')}
                            />
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          ))}

          <div style={sx(CARD)}>
            <div style={sxc(SERIF_H, 'margin-bottom:2px')}>
              Structures, electrical &amp; mechanical works
            </div>
            <div style={sxc(HINT, 'margin-bottom:16px')}>
              Number of units that <strong>cleared each stage during this week</strong>. Do not
              repeat units already reported in an earlier week.
            </div>
            <div style={sx('display:grid;grid-template-columns:repeat(auto-fit,minmax(290px,1fr));gap:16px')}>
              {UNITS.map((g) => {
                const comp = engine.compPcts()[g.k];
                return (
                  <div
                    key={g.k}
                    style={sx('border:1px solid #e4eae8;border-radius:6px;padding:14px;background:#fbfcfc')}
                  >
                    <div
                      style={sx(
                        'display:flex;justify-content:space-between;align-items:baseline;gap:8px;margin-bottom:8px',
                      )}
                    >
                      <div style={sx('font-size:13.5px;font-weight:600;line-height:1.3')}>{g.n}</div>
                      <div style={sx("font-family:'IBM Plex Mono',monospace;font-size:13px;color:#0f5c52")}>
                        {pct1(comp)}%
                      </div>
                    </div>
                    <div style={sx('font-size:11px;color:#5a6b68;margin-bottom:10px')}>
                      {g.total} {g.unit} in scope ·{' '}
                      {Math.min(g.total, engine.cumStage(g.k, g.stages.length - 1))} fully complete
                    </div>
                    <table style={sx('width:100%;border-collapse:collapse')}>
                      <tbody>
                        {g.stages.map((st, i) => (
                          <tr key={st[0]}>
                            <td style={sx('padding:4px 0;font-size:12.5px')}>{st[0]}</td>
                            <td
                              style={sx(
                                'padding:4px 6px;font-size:11px;color:#95a3a0;text-align:right;white-space:nowrap',
                              )}
                            >
                              {st[1]}%
                            </td>
                            <td style={sx('padding:4px 0;width:66px')}>
                              <input
                                type="number"
                                aria-label={`${g.n} — ${st[0]}, units this week`}
                                value={String(draft.units[g.k]?.[i] ?? '')}
                                disabled={dis}
                                onChange={(e) =>
                                  edit((x) => {
                                    x.units[g.k] ??= {};
                                    if (e.target.value === '') delete x.units[g.k][i];
                                    else x.units[g.k][i] = e.target.value;
                                    if (!Object.keys(x.units[g.k]).length) delete x.units[g.k];
                                  })
                                }
                                style={sx(
                                  'padding:7px;border:1px solid #d3dcda;border-radius:4px;font-size:14px;width:100%;box-sizing:border-box;text-align:right;background:#fffdf3',
                                )}
                              />
                            </td>
                            <td
                              style={sx(
                                "padding:4px 0 4px 8px;font-size:12px;color:#5a6b68;white-space:nowrap;font-family:'IBM Plex Mono',monospace",
                              )}
                            >
                              {Math.min(g.total, engine.cumStage(g.k, i))}/{g.total}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    <input
                      type="text"
                      aria-label={`${g.n} — remarks`}
                      value={draft.unitRemarks[g.k] ?? ''}
                      disabled={dis}
                      onChange={(e) => edit((x) => void (x.unitRemarks[g.k] = e.target.value))}
                      placeholder="Remarks — which unit / location"
                      style={sx(
                        'margin-top:10px;padding:8px;border:1px solid #d3dcda;border-radius:4px;font-size:13.5px;width:100%;box-sizing:border-box',
                      )}
                    />
                  </div>
                );
              })}
            </div>
          </div>

          {/* ------------------------------------------------- material */}
          <div style={sx(CARD)}>
            <div
              style={sx(
                'display:flex;justify-content:space-between;align-items:center;margin-bottom:12px;gap:12px;flex-wrap:wrap',
              )}
            >
              <div>
                <div style={sx(SERIF_H)}>Material mobilisation at site</div>
                <div style={sx(HINT)}>A leading indicator only — it does not move the progress bar.</div>
              </div>
              <button
                type="button"
                disabled={dis}
                onClick={() =>
                  edit((d) =>
                    void d.material.push({
                      id: `m${Date.now()}${Math.random().toString(36).slice(2, 6)}`,
                      site: '',
                      cement: 'No',
                      sand: 'No',
                      crush: 'No',
                      steel: 'No',
                      shuttering: 'No',
                      rem: '',
                    } as MaterialRow),
                  )
                }
                style={sx(BTN_SOFT)}
              >
                + Add site
              </button>
            </div>
            <div style={sx('overflow-x:auto')}>
              <table style={sx('width:100%;border-collapse:collapse;min-width:760px')}>
                <thead>
                  <tr>
                    <th style={sx(TH_S)}>Site / structure</th>
                    {MATCOLS.map(([, label]) => (
                      <th key={label} style={sx(TH_S)}>
                        {label}
                      </th>
                    ))}
                    <th style={sx(TH_S)}>Remarks</th>
                    <th style={sx('border-bottom:1px solid #dbe3e0')} />
                  </tr>
                </thead>
                <tbody>
                  {draft.material.map((m, idx) => (
                    <tr key={m.id ?? idx}>
                      <td style={sx(TD_CELL)}>
                        <input
                          type="text"
                          aria-label="Site or structure"
                          value={m.site}
                          disabled={dis}
                          onChange={(e) => edit((d) => void (d.material[idx].site = e.target.value))}
                          style={sxc(INPUT_CELL, 'min-width:110px')}
                        />
                      </td>
                      {MATCOLS.map(([key, label]) => (
                        <td key={key} style={sx(TD_CELL)}>
                          <select
                            aria-label={`${label} at ${m.site || 'site'}`}
                            value={m[key] ?? 'No'}
                            disabled={dis}
                            onChange={(e) => edit((d) => void (d.material[idx][key] = e.target.value))}
                            style={sx(
                              'padding:7px;border:1px solid #d3dcda;border-radius:4px;font-size:13px;background:#fff',
                            )}
                          >
                            <option value="No">No</option>
                            <option value="Partial">Partial</option>
                            <option value="Yes">Yes</option>
                          </select>
                        </td>
                      ))}
                      <td style={sx(TD_CELL)}>
                        <input
                          type="text"
                          aria-label="Material remarks"
                          value={m.rem}
                          disabled={dis}
                          onChange={(e) => edit((d) => void (d.material[idx].rem = e.target.value))}
                          style={sxc(INPUT_CELL, 'min-width:130px')}
                        />
                      </td>
                      <td style={sx(TD_CELL)}>
                        <button
                          type="button"
                          aria-label="Remove this site"
                          disabled={dis}
                          onClick={() => edit((d) => void d.material.splice(idx, 1))}
                          style={sx(BTN_X)}
                        >
                          ×
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* -------------------------------------------------- expenses */}
          <div style={sx(CARD)}>
            <div
              style={sx(
                'display:flex;justify-content:space-between;align-items:center;margin-bottom:12px;gap:12px;flex-wrap:wrap',
              )}
            >
              <div>
                <div style={sx(SERIF_H)}>Payments made this week</div>
                <div style={sx(HINT)}>Each entry feeds the financial report automatically.</div>
              </div>
              <button
                type="button"
                disabled={dis}
                onClick={() =>
                  edit((d) =>
                    void d.expenses.push({
                      id: `e${Date.now()}${Math.random().toString(36).slice(2, 6)}`,
                      date: d.end || '',
                      cat: 'civil',
                      desc: '',
                      amt: '',
                      vendor: '',
                    }),
                  )
                }
                style={sx(BTN_SOFT)}
              >
                + Add payment
              </button>
            </div>
            <div style={sx('overflow-x:auto')}>
              <table style={sx('width:100%;border-collapse:collapse;min-width:780px')}>
                <thead>
                  <tr>
                    <th style={sxc(TH_S, 'width:150px')}>Date</th>
                    <th style={sxc(TH_S, 'width:140px')}>Category</th>
                    <th style={sx(TH_S)}>Description</th>
                    <th style={sxc(TH_S, 'width:110px')}>Rs Mn</th>
                    <th style={sx(TH_S)}>Paid to</th>
                    <th style={sx('border-bottom:1px solid #dbe3e0')} />
                  </tr>
                </thead>
                <tbody>
                  {draft.expenses.map((x, idx) => (
                    <tr key={x.id ?? idx}>
                      <td style={sx(TD_CELL)}>
                        <input
                          type="date"
                          aria-label="Payment date"
                          value={x.date}
                          disabled={dis}
                          onChange={(e) => edit((d) => void (d.expenses[idx].date = e.target.value))}
                          style={sx(
                            'padding:8px;border:1px solid #d3dcda;border-radius:4px;font-size:13.5px;width:100%;box-sizing:border-box',
                          )}
                        />
                      </td>
                      <td style={sx(TD_CELL)}>
                        <select
                          aria-label="Head of account"
                          value={x.cat}
                          disabled={dis}
                          onChange={(e) =>
                            edit((d) => void (d.expenses[idx].cat = e.target.value as typeof x.cat))
                          }
                          style={sx(
                            'padding:8px;border:1px solid #d3dcda;border-radius:4px;font-size:13.5px;width:100%;background:#fff',
                          )}
                        >
                          <option value="civil">Civil</option>
                          <option value="mech">Mechanical</option>
                          <option value="elec">Electrical</option>
                          <option value="misc">Miscellaneous</option>
                        </select>
                      </td>
                      <td style={sx(TD_CELL)}>
                        <input
                          type="text"
                          aria-label="Particulars"
                          value={x.desc}
                          disabled={dis}
                          onChange={(e) => edit((d) => void (d.expenses[idx].desc = e.target.value))}
                          style={sxc(INPUT_CELL, 'min-width:150px')}
                        />
                      </td>
                      <td style={sx(TD_CELL)}>
                        <input
                          type="number"
                          aria-label="Amount in Rs million"
                          value={String(x.amt ?? '')}
                          disabled={dis}
                          onChange={(e) => edit((d) => void (d.expenses[idx].amt = e.target.value))}
                          style={sx(INPUT_NUM)}
                        />
                      </td>
                      <td style={sx(TD_CELL)}>
                        <input
                          type="text"
                          aria-label="Paid to"
                          value={x.vendor}
                          disabled={dis}
                          onChange={(e) => edit((d) => void (d.expenses[idx].vendor = e.target.value))}
                          style={sxc(INPUT_CELL, 'min-width:120px')}
                        />
                      </td>
                      <td style={sx(TD_CELL)}>
                        <button
                          type="button"
                          aria-label="Remove this payment"
                          disabled={dis}
                          onClick={() => edit((d) => void d.expenses.splice(idx, 1))}
                          style={sx(BTN_X)}
                        >
                          ×
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* ------------------------------------- photographs & narrative */}
          <div style={sx('display:grid;grid-template-columns:repeat(auto-fit,minmax(300px,1fr));gap:18px')}>
            <div style={sx(CARD)}>
              <div style={sx(SERIF_H)}>Site photographs</div>
              <div style={sxc(HINT, 'margin-bottom:10px')}>
                Mark each photograph with the part of the works it shows, so it appears in that
                component's photographic record.
              </div>
              <input
                type="file"
                accept="image/*"
                multiple
                capture="environment"
                disabled={dis}
                aria-label="Add site photographs"
                onChange={async (e) => {
                  const files = Array.from(e.target.files ?? []);
                  e.target.value = '';
                  if (!files.length) return;
                  if (dirty.current) await flush();
                  await mutate(() => api.uploadPhotos(week.id, files));
                }}
                style={sx('font-size:13px;margin-bottom:12px;max-width:100%')}
              />
              <div style={sx('display:grid;grid-template-columns:repeat(auto-fill,minmax(105px,1fr));gap:10px')}>
                {week.photos.map((p) => (
                  <div key={p.id} style={sx('display:flex;flex-direction:column;gap:5px')}>
                    <div style={sx('position:relative')}>
                      <img
                        src={p.src}
                        alt={p.cap || 'site photograph'}
                        style={sx(
                          'width:100%;height:82px;object-fit:cover;border-radius:4px;border:1px solid #dbe3e0',
                        )}
                      />
                      <button
                        type="button"
                        aria-label="Remove photograph"
                        disabled={dis}
                        onClick={() => void mutate(() => api.deletePhoto(p.id))}
                        style={sx(
                          'position:absolute;top:3px;right:3px;background:rgba(255,255,255,.9);border:1px solid #dbe3e0;border-radius:3px;color:#a3372c;cursor:pointer;font-size:13px;line-height:1;padding:3px 6px',
                        )}
                      >
                        ×
                      </button>
                    </div>
                    <input
                      type="text"
                      aria-label="Photograph caption"
                      defaultValue={p.cap}
                      disabled={dis}
                      onBlur={(e) => {
                        if (e.target.value !== p.cap) {
                          void mutate(() => api.captionPhoto(p.id, e.target.value));
                        }
                      }}
                      style={sx(
                        'padding:5px 6px;border:1px solid #d3dcda;border-radius:4px;font-size:12px;width:100%;box-sizing:border-box',
                      )}
                    />
                    <select
                      aria-label="Part of the works shown"
                      value={p.comp}
                      disabled={dis}
                      onChange={(e) => void mutate(() => api.tagPhoto(p.id, e.target.value))}
                      style={sx(
                        'padding:4px 5px;border:1px solid #d3dcda;border-radius:4px;font-size:11.5px;width:100%;box-sizing:border-box;background:#fff;color:#33403d',
                      )}
                    >
                      <option value="">Site in general</option>
                      {componentList().map((c) => (
                        <option key={c.k} value={c.k}>
                          {c.n}
                        </option>
                      ))}
                    </select>
                  </div>
                ))}
              </div>
            </div>

            <div style={sxc(CARD, 'display:flex;flex-direction:column;gap:12px')}>
              <div>
                <div style={sx(SERIF_H)}>Miscellaneous work &amp; narrative</div>
                <div style={sx(HINT)}>Appears verbatim in the weekly report.</div>
              </div>
              <textarea
                aria-label="Narrative for the week"
                value={draft.narrative}
                disabled={dis}
                rows={8}
                onChange={(e) => edit((d) => void (d.narrative = e.target.value))}
                placeholder="Summary of the week in plain language — what moved, what stalled, what is planned next week."
                style={sx(
                  'padding:10px;border:1px solid #d3dcda;border-radius:4px;font-size:14px;line-height:1.55;resize:vertical;width:100%;box-sizing:border-box',
                )}
              />
              {editable ? (
                <button
                  type="button"
                  onClick={async () => {
                    if (!window.confirm(`Delete Week ${week.no} and everything recorded in it?`)) return;
                    savedSeq.current = editSeq.current;
                    window.clearTimeout(timer.current);
                    const ok = await mutate(() => api.deleteWeek(week.id));
                    if (ok) {
                      setSelected('');
                      await refresh();
                    }
                  }}
                  style={sxc(BTN_DANGER, 'align-self:flex-start')}
                >
                  Delete this week
                </button>
              ) : null}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
